import { Tooltip, Text, FaroTextVariant } from "@flat-ui";
import { useIsOverflowing } from "@hooks/use-is-overflowing";
import { Box, SxProps } from "@mui-wrapper";

interface Props {
  /** Text before the truncated name */
  prefix?: string;

  /** Text to be displayed */
  text: string;

  /** Tooltip text shown on hover. Defaults to `name` */
  tooltipText?: string;

  /** Text after the truncated name */
  suffix?: string;

  /** Number of lines to show before truncating */
  lines?: number;

  /** Custom styles for the text component */
  customSx?: SxProps;

  /** Text variant for the Text component. Defaults to `bodyM` */
  textVariant?: FaroTextVariant;
}

/**
 * Displays text with ellipsis truncation, showing a tooltip on hover only when the text overflows its container.
 * Supports optional prefix and suffix rendered outside the truncated area
 */
export function TextWithTooltip({
  prefix,
  text,
  suffix,
  customSx,
  tooltipText,
  textVariant = FaroTextVariant.bodyM,
}: Props): React.JSX.Element {
  const { textRef, isOverflowing } = useIsOverflowing<HTMLElement>();

  return (
    <>
      {prefix && (
        <Text
          variant={textVariant}
          sx={{ width: isOverflowing ? "100%" : "fit-content" }}
        >
          {`${prefix} `}
        </Text>
      )}
      <Tooltip
        title={
          // Use a Box to ensure the tooltip text wraps properly and doesn't overflow the viewport
          <Box
            component="div"
            sx={{
              wordBreak: "break-all",
            }}
          >
            {tooltipText ?? text}
          </Box>
        }
        disableHoverListener={!isOverflowing}
        enterDelay={500}
      >
        <Text
          variant={textVariant}
          ref={textRef}
          sx={{
            minWidth: 0,
            display: "inline-block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: prefix || suffix ? "12rem" : "100%",
            verticalAlign: "middle",
            ...customSx,
          }}
        >
          {text}
        </Text>
      </Tooltip>
      {suffix && <Text variant={textVariant}>{` ${suffix}`}</Text>}
    </>
  );
}
